import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-cropper';
import noop from 'lodash/noop';

import { flow, split, last, first, get } from 'lodash/fp';

import ImageUploaderCard from './ImageUploaderCard';

import { createAttachmentWithPresignedUrl, uploadImageToS3 } from '../../utils/request';
import { blobToFile } from '../../utils/helpers';
import MBRModal from '../MBRModal/MBRModal';
import Button from '../Button';

const CONTENT_TYPE = 'image/*';

// Default cropper parameters
const cropperParams = {
  movable: false,
  responsive: false,
  restore: true,
  rotatable: false,
  scalable: false,
  zoomable: false,
  checkCrossOrigin: true,
};

const getAttachmentByUrl = value =>
  value && {
    file: {
      name: flow(
        split('/'),
        last,
        split('?'),
        first,
      )(value),
    },
    url: value,
  };

// const VALUE_TYPE = PropTypes.shape({
//   attachment: PropTypes.shape({
//     id: PropTypes.string,
//   }),
//   cropRegion: PropTypes.object,
//   type: PropTypes.string,
//   id: PropTypes.string,
//   dimensions: PropTypes.array,
// });

class ImageUploader extends Component {
  static propTypes = {
    aspectRatio: PropTypes.number,
    crop: PropTypes.bool,
    canvasWidth: PropTypes.number,
    canvasHeight: PropTypes.number,
    label: PropTypes.string,
    overlay: PropTypes.bool,
    onChange: PropTypes.func,
    value: PropTypes.string,
  };

  static defaultProps = {
    value: null,
    onChange: noop,
    crop: false,
    aspectRatio: NaN,
    label: '',
    overlay: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      showCropper: false,
      uploads: 0,
      attachment: getAttachmentByUrl(props.value),
    };

    this.input = document.createElement('input');
    this.input.type = 'file';
    this.input.accept = CONTENT_TYPE;
    this.input.onchange = this.onFilePickerInputChange;
  }

  showFileDialog = (...args) => {
    this.input.click(...args);
  };

  cropButtons = () => (
    <div className="modal_buttons">
      <Button className="modal_button -large" type="button" onClick={this.onSaveCrop}>
        Save
      </Button>
      <Button className="modal_button -empty" type="button" onClick={this.hideCropper}>
        Cancel
      </Button>
    </div>
  );

  componentWillUnmount() {
    delete this.input;
  }

  onRemove = () => {
    this.input.value = '';
    this.props.onChange(null);
  };

  onSaveCrop = () => {
    const { aspectRatio, canvasWidth, canvasHeight } = this.props;
    const fileName = this.state.attachment.file.name;
    this.hideCropper();
    this.crop.getCroppedCanvas().toBlob(async file => {
      const fileToUpload = blobToFile(file, fileName);
      const dimensions = await estimateFileDimensions(file);
      const cropRegion = defaultCropRegion(aspectRatio, dimensions);
      const attachment = await this.uploadImage(fileToUpload);

      this.props.onChange(attachment.url);
      // this.updateValueAndTriggerOnChange({ attachment, cropRegion, dimensions }, true);
    });
  };

  onFilePickerInputChange = async ({ target }) => {
    const file = target.files[0];
    if (!file) return;
    const { cropRequired } = this.props;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const attachment = { file: file, url: reader.result };

      if (cropRequired) {
        this.setState({ attachment });
        this.showCropper();
      } else {
        this.props.onChange(attachment.url);
        // this.updateValueAndTriggerOnChange({ attachment });
      }
    };
  };

  async uploadImage(file) {
    return this.withLoadingIndicator(async () => {
      const { presignedUrl, format } = await createAttachmentWithPresignedUrl(file.name);
      await uploadImageToS3(presignedUrl, file, { contentType: format || CONTENT_TYPE });
      return { file: file, url: presignedUrl.split('?X')[0] };
    });
  }

  renderCropper() {
    const { cropRegion, attachment } = this.state;
    const { crop } = this.props;
    return attachment && crop ? (
      <Cropper
        className="Cropper cropper"
        ref={e => (this.crop = e)}
        src={attachment.url}
        data={cropRegion}
        aspectRatio={this.props.aspectRatio}
        {...cropperParams}
      />
    ) : null;
  }

  modalOverlayRef(ref) {
    if (ref) {
      ref.onmousedown = e => {
        e.stopPropagation();
      };
    }
  }

  render() {
    return (
      <div className="image-upload mt-10">
        <label className="block color-gray mb-5">{this.props.label}</label>
        <ImageUploaderCard
          type={this.props.type}
          imgUrl={this.props.value}
          isLoading={!!this.state.uploads}
          label={this.props.label}
          onChange={this.showCropper}
          onRemove={this.onRemove}
          onUpload={this.showFileDialog}
          hasError={this.props.hasError}
        />
        <MBRModal
          contentLabel="Crop Image"
          className="-large"
          overlayRef={this.modalOverlayRef}
          visible={this.state.showCropper}
          onRequestClose={this.hideCropper}
          shouldCloseOnOverlayClick={false}
        >
          {this.renderCropper()}
          {this.cropButtons()}
        </MBRModal>
      </div>
    );
  }

  showCropper = () => {
    this.setState({ showCropper: true });
  };

  hideCropper = () => {
    this.setState({ showCropper: false });
  };

  async withLoadingIndicator(asyncCallback) {
    this.setState({ uploads: this.state.uploads + 1 });
    try {
      return await asyncCallback();
    } finally {
      this.setState({ uploads: this.state.uploads - 1 });
    }
  }

  originalImageForCropper() {
    return get('url', this.state.attachment);
  }
}

async function estimateFileDimensions(file) {
  const img = new Image();
  img.src = URL.createObjectURL(file);
  const [origWidth, origHeight] = await new Promise(resolve => {
    img.onload = () => {
      resolve([img.width, img.height]);
    };
  });
  URL.revokeObjectURL(img.src);
  return [origWidth, origHeight];
}

function normalizeCropData(cropData) {
  let { x, y, width, height } = cropData;

  if (x < 0) x = 0;
  if (y < 0) y = 0;

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(width),
    height: Math.round(height),
  };
}

export function defaultCropRegion(aspectRatio, [origWidth, origHeight]) {
  if (!aspectRatio || !origHeight) return null;

  let height = origHeight;
  if (aspectRatio > origWidth / origHeight) {
    let height = origWidth / aspectRatio;
  }

  const width = height * aspectRatio;
  return normalizeCropData({
    y: (origHeight - height) / 2,
    x: (origWidth - width) / 2,
    height,
    width,
  });
}

export default ImageUploader;



// WEBPACK FOOTER //
// ./app/javascript/common/components/ImageUploader/ImageUploader.js